'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { planService } from '@/services/planService';
import { useAuth } from '@/contexts/AuthContext';
import PlanVisualization from './PlanVisualization';
import RecommendationDetails from './RecommendationDetails';
import IncomeProjections from './IncomeProjections';
import ImplementationTimeline from './ImplementationTimeline';
import MaterialsList from './MaterialsList';
import PlanActions from './PlanActions';
import CustomizationPanel from './CustomizationPanel';

interface UserInputs {
  plan_name?: string;
  dimensions: string;
  location: string;
  climate?: string;
  sunlight: string;
  budget: string;
  objective: string;
  details?: string;
}

interface PlantZone {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  plantType: 'herbs' | 'leafy' | 'fruiting';
  color: string;
}

interface PlantRecommendation {
  id: string;
  name: string;
  variety: string;
  quantity: number;
  spacing: string;
  plantingTime: string;
  harvestTime: string;
  expectedYield: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  sunRequirement: string;
  waterRequirement: string;
  growthTime: string;
}

interface MonthlyProjection {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface TimelineTask {
  id: string;
  title: string;
  description: string;
  month: string;
  week: string;
  category: 'Setup' | 'Planting' | 'Maintenance' | 'Harvest';
  priority: 'High' | 'Medium' | 'Low';
  estimatedTime: string;
  materials?: string[];
}

interface Material {
  id: string;
  name: string;
  category: 'Seeds' | 'Tools' | 'Soil' | 'Containers' | 'Infrastructure' | 'Other';
  quantity: string;
  estimatedCost: number;
  priority: 'Essential' | 'Recommended' | 'Optional';
  supplier?: string;
  notes?: string;
}

interface PlantOption {
  id: string;
  name: string;
  variety: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  expectedYield: string;
  growthTime: string;
}

interface StructuredPlanData {
  plantZones?: PlantZone[];
  recommendations?: PlantRecommendation[];
  monthlyProjections?: MonthlyProjection[];
  timelineTasks?: TimelineTask[];
  materials?: Material[];
  currentPlants?: PlantOption[];
  availableAlternatives?: PlantOption[];
}

interface AIPlanInteractiveProps {
  // Props would be passed from page.tsx
}

const AIPlanInteractive = ({}: AIPlanInteractiveProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [aiPlan, setAiPlan] = useState<string>('');
  const [userInputs, setUserInputs] = useState<UserInputs | null>(null);
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(true);
  const [error, setError] = useState<string>('');
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [structuredData, setStructuredData] = useState<StructuredPlanData | null>(null);

  const auth = useAuth();

  // Updated AI plan generation function using NVIDIA API via API route
  // Using useCallback to ensure it's stable and available for useEffect
  const generatePlanWithAI = useCallback(async (inputs: UserInputs) => {
    setIsGeneratingPlan(true);
    setError('');
    
    try {
      console.log('Generating plan with inputs:', inputs);
      // Call the API route which handles NVIDIA API on the server side
      const response = await fetch('/api/generate-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dimensions: inputs.dimensions,
          location: inputs.location,
          climate: inputs.climate,
          sunlight: inputs.sunlight,
          budget: inputs.budget,
          objective: inputs.objective,
          details: inputs.details,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('API error response:', errorData);
        throw new Error(errorData.message || `Failed to generate plan (${response.status})`);
      }

      const data = await response.json();
      console.log('Plan generated successfully:', data);
      if (data.plan) {
        setAiPlan(data.plan);
        // Store structured data if available
        if (data.structuredData) {
          console.log('Structured data received:', data.structuredData);
          setStructuredData(data.structuredData);
        } else {
          console.warn('No structured data in response');
        }
      } else {
        throw new Error('No plan data in response');
      }
    } catch (error: any) {
      console.error('Error generating AI plan:', error);
      setError(error.message || 'Failed to generate AI plan. Please try again.');
    } finally {
      setIsGeneratingPlan(false);
    }
  }, []);

  // Extract user inputs from URL parameters or load saved plan by ID
  useEffect(() => {
    setIsHydrated(true);
    
    const planId = searchParams.get('planId');
    const plan_name = searchParams.get('plan_name');
    const dimensions = searchParams.get('dimensions');
    const location = searchParams.get('location');
    const climate = searchParams.get('climate');
    const sunlight = searchParams.get('sunlight');
    const budget = searchParams.get('budget');
    const objective = searchParams.get('objective');
    const details = searchParams.get('details');

    console.log('URL params:', { planId, dimensions, location, climate, sunlight, budget, objective });

    // If planId is provided, fetch the saved plan from database
    if (planId) {
      const fetchSavedPlan = async () => {
        try {
          const result = await planService.fetchById(planId);
          if (result.data) {
            const plan = result.data;
            const inputs: UserInputs = {
              plan_name: plan.plan_name || undefined,
              dimensions: plan.dimensions,
              location: plan.location,
              climate: plan.climate || undefined,
              sunlight: plan.sunlight,
              budget: plan.budget,
              objective: plan.objective,
              details: plan.details || undefined
            };
            setUserInputs(inputs);
            // Use the saved AI response if available
            if (plan.ai_response) {
              setAiPlan(plan.ai_response);
            } else {
              // Re-generate if no response saved
              generatePlanWithAI(inputs);
            }
          } else {
            setError('Failed to load saved plan. Please go back to My Plans.');
          }
        } catch (err) {
          console.error('Error fetching plan:', err);
          setError('Failed to load saved plan. Please go back to My Plans.');
        }
        setIsLoadingFromUrl(false);
      };
      fetchSavedPlan();
    } else if (dimensions && location && climate && sunlight && budget && objective) {
      // Load from form query params
      const inputs: UserInputs = {
        plan_name: plan_name || undefined,
        dimensions,
        location,
        climate: climate || undefined,
        sunlight,
        budget,
        objective,
        details: details || undefined
      };
      setUserInputs(inputs);
      
      // Auto-generate plan when inputs are available
      console.log('Calling generatePlanWithAI with inputs:', inputs);
      generatePlanWithAI(inputs);
      setIsLoadingFromUrl(false);
    } else {
      console.log('Missing required inputs. Available:', { dimensions, location, climate, sunlight, budget, objective });
      setError('Missing required inputs. Please go back to the form.');
      setIsLoadingFromUrl(false);
    }
  }, [searchParams, generatePlanWithAI]);

  const handleSavePlan = async (planId: string, planName: string) => {
    if (!auth.isAuthenticated) {
      // Redirect to login so user can sign in before saving
      router.push('/login');
      return;
    }

    if (!userInputs || !aiPlan) {
      setError('No plan data available to save');
      return;
    }

    setIsSavingPlan(true);
    setError('');

    try {
      const planData = {
        plan_name: userInputs.plan_name,
        dimensions: userInputs.dimensions,
        location: userInputs.location,
        climate: userInputs.climate,
        sunlight: userInputs.sunlight,
        budget: userInputs.budget,
        objective: userInputs.objective,
        details: userInputs.details,
        ai_response: aiPlan
      };

      const result = await planService.create(planData);
      
      if (result.error) {
        setError(`Failed to save plan: ${result.error.message}`);
        return;
      }

      // Show success and redirect to saved plans
      router.push('/saved-plans');
    } catch (error) {
      setError('An unexpected error occurred while saving the plan');
    } finally {
      setIsSavingPlan(false);
    }
  };

  const handleExportPDF = async (planId: string) => {
    // Simulate PDF generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('PDF exported for plan:', planId);
  };

  const handleSharePlan = (planId: string) => {
    if (navigator.share) {
      navigator.share({
        title: 'My Space Optimization Plan',
        text: 'Check out my personalized space optimization plan!',
        url: window.location.href
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleModifyInputs = () => {
    router.push('/input-form');
  };

  const handlePlantChange = (oldPlantId: string, newPlantId: string) => {
    console.log('Plant changed:', oldPlantId, 'to', newPlantId);
  };

  const handleRecalculate = () => {
    if (userInputs) {
      generatePlanWithAI(userInputs);
    }
  };

  if (!isHydrated || isLoadingFromUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-muted-foreground">Loading your inputs...</div>
        </div>
      </div>
    );
  }

  if (error && !userInputs) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => router.push('/input-form')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Input Form
          </button>
        </div>
      </div>
    );
  }

  if (isGeneratingPlan) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold mb-2">Generating Your AI Plan</h3>
          <p className="text-muted-foreground mb-4">
            Using NVIDIA Llama 3.3 70B to analyze your space and create personalized recommendations...
          </p>
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Processing: {userInputs?.dimensions} {userInputs?.location} • {userInputs?.objective} focus
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!aiPlan && !isGeneratingPlan) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          {error ? (
            <>
              <div className="text-red-500 mb-4 font-semibold">{error}</div>
              <button
                onClick={() => userInputs && generatePlanWithAI(userInputs)}
                className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors mr-2"
              >
                Try Again
              </button>
              <button
                onClick={() => router.push('/input-form')}
                className="border border-border text-foreground px-6 py-2 rounded-lg hover:bg-muted transition-colors"
              >
                Go to Input Form
              </button>
            </>
          ) : (
            <>
              <div className="text-muted-foreground mb-4">No plan generated yet</div>
              {userInputs && (
                <button
                  onClick={() => generatePlanWithAI(userInputs)}
                  className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Generate Plan
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Use AI-generated structured data, fallback to calculated defaults if not available
  const spaceData = {
    width: parseInt(userInputs?.dimensions?.split('x')[0]?.trim() || userInputs?.dimensions?.split('m')[0]?.trim() || '12'),
    height: parseInt(userInputs?.dimensions?.split('x')[1]?.trim() || userInputs?.dimensions?.split('m')[1]?.trim() || '8'),
    spaceType: userInputs?.location || "Space"
  };

  // Use AI-generated plant zones or fallback to calculated defaults
  const plantZones = structuredData?.plantZones || [
    {
      id: "zone1",
      name: "Herbs",
      x: 1,
      y: 1,
      width: Math.floor(spaceData.width * 0.3),
      height: Math.floor(spaceData.height * 0.3),
      plantType: "herbs" as const,
      color: "#4A7C59"
    },
    {
      id: "zone2",
      name: "Leafy Greens",
      x: Math.floor(spaceData.width * 0.5),
      y: 1,
      width: Math.floor(spaceData.width * 0.4),
      height: Math.floor(spaceData.height * 0.4),
      plantType: "leafy" as const,
      color: "#7B9A6B"
    },
    {
      id: "zone3",
      name: userInputs?.objective === 'income' ? "High-Value Crops" : "Vegetables",
      x: 1,
      y: Math.floor(spaceData.height * 0.5),
      width: Math.floor(spaceData.width * 0.4),
      height: Math.floor(spaceData.height * 0.4),
      plantType: "fruiting" as const,
      color: "#F4A261"
    }
  ];

  // Use AI-generated recommendations or fallback
  const recommendations = structuredData?.recommendations || [
    {
      id: "1",
      name: userInputs?.sunlight === 'high' ? "Tomatoes" : "Lettuce",
      variety: userInputs?.sunlight === 'high' ? "Cherry Hybrid" : "Buttercrunch",
      quantity: userInputs?.objective === 'income' ? 6 : 4,
      spacing: "12 inches apart",
      plantingTime: "March - April",
      harvestTime: "June - October",
      expectedYield: userInputs?.sunlight === 'high' ? "15-20 lbs per plant" : "1 head per plant",
      difficulty: (userInputs?.sunlight === 'low' ? "Easy" : "Medium") as 'Easy' | 'Medium' | 'Hard',
      sunRequirement: userInputs?.sunlight === 'high' ? "6-8 hours" : "4-6 hours",
      waterRequirement: "Regular watering",
      growthTime: userInputs?.sunlight === 'high' ? "70-85 days" : "45-60 days"
    },
    {
      id: "2",
      name: "Herbs",
      variety: userInputs?.objective === 'income' ? "Premium Basil Mix" : "Kitchen Herbs",
      quantity: userInputs?.objective === 'income' ? 12 : 8,
      spacing: "6 inches apart",
      plantingTime: "April - May",
      harvestTime: "June - September",
      expectedYield: "2-3 cups per plant",
      difficulty: "Easy" as const,
      sunRequirement: "4-6 hours",
      waterRequirement: "Moderate watering",
      growthTime: "30-45 days"
    }
  ];

  // Use AI-generated monthly projections or fallback
  const monthlyProjections = structuredData?.monthlyProjections || [
    { month: "Jan", revenue: 0, expenses: 150, profit: -150 },
    { month: "Feb", revenue: 0, expenses: 100, profit: -100 },
    { month: "Mar", revenue: 25, expenses: 75, profit: -50 },
    { month: "Apr", revenue: 50, expenses: 50, profit: 0 },
    { month: "May", revenue: userInputs?.objective === 'income' ? 200 : 125, expenses: 40, profit: userInputs?.objective === 'income' ? 160 : 85 },
    { month: "Jun", revenue: userInputs?.objective === 'income' ? 350 : 200, expenses: 30, profit: userInputs?.objective === 'income' ? 320 : 170 },
  ];

  // Use AI-generated timeline tasks or fallback
  const timelineTasks = structuredData?.timelineTasks || [
    {
      id: "1",
      title: "Setup Containers and Soil",
      description: "Prepare containers, add drainage, and fill with quality potting mix",
      month: "March",
      week: "Week 1-2",
      category: "Setup" as const,
      priority: "High" as const,
      estimatedTime: "4-6 hours",
      materials: ["Containers", "Potting soil", "Drainage materials"]
    },
    {
      id: "2",
      title: `Plant ${userInputs?.objective === 'income' ? 'High-Value' : 'Primary'} Crops`,
      description: `Start ${userInputs?.objective === 'income' ? 'herbs and microgreens for income generation' : 'main vegetables for food production'}`,
      month: "March",
      week: "Week 3-4",
      category: "Planting" as const,
      priority: "High" as const,
      estimatedTime: "2-3 hours",
      materials: ["Seeds", "Plant labels", "Watering can"]
    }
  ];

  // Use AI-generated materials or fallback
  const materials = structuredData?.materials || [
    {
      id: "1",
      name: "Containers",
      category: "Containers" as const,
      quantity: `${Math.ceil(spaceData.width * spaceData.height / 10)} pieces`,
      estimatedCost: parseInt(userInputs?.budget?.replace(/[^0-9]/g, '') || '500') * 0.3,
      priority: "Essential" as const,
      supplier: "Local nursery"
    },
    {
      id: "2",
      name: "Premium Potting Mix",
      category: "Soil" as const,
      quantity: `${Math.ceil(spaceData.width * spaceData.height / 8)} bags`,
      estimatedCost: parseInt(userInputs?.budget?.replace(/[^0-9]/g, '') || '500') * 0.4,
      priority: "Essential" as const,
      supplier: "Garden center"
    }
  ];

  // Use AI-generated plant options or fallback
  const currentPlants = structuredData?.currentPlants || recommendations.slice(0, 2).map((rec, idx) => ({
    id: rec.id,
    name: rec.name,
    variety: rec.variety,
    difficulty: rec.difficulty,
    expectedYield: rec.expectedYield,
    growthTime: rec.growthTime
  }));

  const availableAlternatives = structuredData?.availableAlternatives || [
    {
      id: "alt1",
      name: userInputs?.sunlight === 'high' ? "Roma Tomatoes" : "Spinach",
      variety: userInputs?.sunlight === 'high' ? "San Marzano" : "Baby Leaf",
      difficulty: (userInputs?.sunlight === 'low' ? "Easy" : "Hard") as 'Easy' | 'Medium' | 'Hard',
      expectedYield: "20-25 lbs per plant",
      growthTime: "85-95 days"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'EyeIcon' },
    { id: 'timeline', label: 'Timeline', icon: 'CalendarDaysIcon' },
    { id: 'materials', label: 'Materials', icon: 'CubeIcon' },
    { id: 'customize', label: 'Customize', icon: 'AdjustmentsHorizontalIcon' }
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* AI Plan Display */}
      <div className="bg-gradient-to-br from-card to-card/80 border border-border rounded-lg p-8 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              {userInputs?.plan_name || 'Your AI-Generated Plan'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Personalized recommendations for optimizing your space
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Generated by NVIDIA Llama 3.3 70B</span>
          </div>
        </div>
        
        {/* Formatted AI Plan Response */}
        <div className="mt-8 space-y-6">
          {aiPlan.split('\n').map((line, idx) => {
            // Handle headers (lines starting with ##)
            if (line.startsWith('## ')) {
              const headerText = line.replace(/^## /, '').trim();
              return (
                <div key={idx}>
                  <h3 className="text-xl font-bold text-primary mb-3 pb-2 border-b-2 border-primary/30">
                    {headerText}
                  </h3>
                </div>
              );
            }
            // Handle bold text (**text**)
            if (line.startsWith('**') && line.endsWith('**')) {
              const boldText = line.replace(/\*\*/g, '').trim();
              return (
                <p key={idx} className="font-semibold text-foreground text-lg">
                  {boldText}
                </p>
              );
            }
            // Handle list items (lines starting with - or *)
            if (line.trim().startsWith('-') || line.trim().startsWith('*')) {
              const itemText = line.trim().replace(/^[-*]\s*/, '').trim();
              // Handle inline bold in list items
              const formattedItem = itemText.replace(/\*\*(.+?)\*\*/g, '$1');
              return (
                <div key={idx} className="flex gap-3 text-foreground leading-relaxed">
                  <span className="text-primary font-bold">•</span>
                  <span>{formattedItem}</span>
                </div>
              );
            }
            // Skip empty lines but add spacing
            if (line.trim() === '') {
              return <div key={idx} className="h-2" />;
            }
            // Regular text
            if (line.trim()) {
              const formattedLine = line
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .trim();
              return (
                <p key={idx} className="text-foreground leading-relaxed text-base">
                  {formattedLine}
                </p>
              );
            }
            return null;
          })}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => handleSavePlan('generated-plan', `${userInputs?.plan_name || userInputs?.location || 'Space'} Plan`)}
            disabled={isSavingPlan}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSavingPlan && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>Save Plan</span>
          </button>
          <button
            onClick={handleRecalculate}
            disabled={isGeneratingPlan}
            className="bg-secondary text-secondary-foreground px-6 py-2 rounded-lg hover:bg-secondary/90 transition-colors"
          >
            Regenerate Plan
          </button>
          <button
            onClick={handleModifyInputs}
            className="border border-border text-foreground px-6 py-2 rounded-lg hover:bg-muted transition-colors"
          >
            Modify Inputs
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <PlanVisualization spaceData={spaceData} plantZones={plantZones} />
            <RecommendationDetails recommendations={recommendations} totalArea={spaceData.width * spaceData.height} />
            <IncomeProjections 
              monthlyData={monthlyProjections}
              totalInvestment={parseInt(userInputs?.budget?.replace(/[^0-9]/g, '') || '500')}
              breakEvenMonth={4}
              yearlyProfit={monthlyProjections.reduce((sum, month) => sum + month.profit, 0) * 2}
            />
          </div>
          <div className="space-y-6">
            <PlanActions
              planId="generated-plan"
              planName={`${userInputs?.location || 'Space'} Plan`}
              onSavePlan={handleSavePlan}
              onExportPDF={handleExportPDF}
              onSharePlan={handleSharePlan}
              onModifyInputs={handleModifyInputs}
              isAuthenticated={auth.isAuthenticated}
            />
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <ImplementationTimeline tasks={timelineTasks} />
      )}

      {activeTab === 'materials' && (
        <MaterialsList materials={materials} />
      )}

      {activeTab === 'customize' && (
        <CustomizationPanel
          currentPlants={currentPlants}
          availableAlternatives={availableAlternatives}
          onPlantChange={handlePlantChange}
          onRecalculate={handleRecalculate}
        />
      )}
    </div>
  );
};

export default AIPlanInteractive;